import _ from 'lodash';
import React from 'react';
import { commitMutation, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Analytics, GlobalNotifications, Intent } from '../../Modules';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import Schedule from '../Sections/Schedule/Schedule';
import DateUtils from '../../../lib/utils/date';

export default class ContractSchedule extends React.Component {
  static propTypes = {
    node: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    readOnly: PropTypes.bool,
    eventSegments: PropTypes.array.isRequired
  };

  constructor(props) {
    super(props);
    this.toggleSearch = this.toggleSearch.bind(this);
    this.closeSearchHandler = this.closeSearchHandler.bind(this);
    this.eventSegmentChangeHandler = this.eventSegmentChangeHandler.bind(this);
    this.updateEventSegment = this.updateEventSegment.bind(this);
    this.resetEditEventSegment = this.resetEditEventSegment.bind(this);
    this.deleteEventSegment = this.deleteEventSegment.bind(this);
    this.newEventSegmentChangeHandler = this.newEventSegmentChangeHandler.bind(
      this
    );
    this.closeNewEventSegmentEditor = this.closeNewEventSegmentEditor.bind(
      this
    );
    this.createEventSegment = this.createEventSegment.bind(this);
    this.addEventSegment = this.addEventSegment.bind(this);
    this.resetAddEventSegment = this.resetAddEventSegment.bind(this);
    this.state = {
      showSearch: false,
      eventSegments: _.cloneDeep(this.props.eventSegments),
      editingNewEventSegment: false
    };
  }

  componentWillReceiveProps(nextProps) {
    // Update eventSegments if any edges are added/deleted
    const obj = _.cloneDeep(this.state);
    obj.eventSegments = _.cloneDeep(
      nextProps.eventSegments
    );
    this.setState({ ...obj });
  }

  toggleSearch() {
    this.setState({ showSearch: !this.state.showSearch });
  }

  closeSearchHandler() {
    this.setState({ showSearch: false });
  }

  eventSegmentChangeHandler(eventSegment, idx) {
    const obj = _.cloneDeep(this.state);
    obj.eventSegments[idx].node = eventSegment;
    this.setState({ ...obj });
  }

  updateEventSegment(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update event information',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated event information',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-contract-event-segment');
    };
    const eventSegment = DateUtils.omitTimeZoneWithoutTimes(_.pick(
      nullifyEmptyStrings(
        this.state.eventSegments[idx].node
      ),
      [
        'id',
        'title',
        'start_at',
        'end_at',
        'time_zone',
        'location',
        'line1',
        'city',
        'province',
        'postal_code',
        'room',
        'notes'
      ]
    ));

    const mutation = graphql`
      mutation Schedule_Contract_updateContractEventSegmentMutation(
        $input: UpdateContractEventSegmentInput!
      ) {
        updateContractEventSegment(input: $input) {
          contract_event_segment {
            id
            title
            start_at
            end_at
            time_zone
            all_day
            location
            line1
            line2
            city
            province
            postal_code
            room
            notes
          }
        }
      }
    `;

    const optimisticResponse = {
      updateContractEventSegment: { contract_event_segment: _.cloneDeep(eventSegment) }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...eventSegment
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  resetEditEventSegment(index) {
    const obj = _.cloneDeep(this.state);
    obj.eventSegments[index] = _.cloneDeep(
      this.props.eventSegments[index]
    );
    this.setState({ ...obj });
  }

  deleteEventSegment(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete agenda item',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      this.resetEditEventSegment();
      GlobalNotifications.show({
        message: 'Successfully deleted agenda item',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-contract-event-segment');
    };

    const mutation = graphql`
      mutation Schedule_Contract_deleteContractEventSegmentMutation(
        $input: DeleteContractEventSegmentInput!
      ) {
        deleteContractEventSegment(input: $input) {
          deletedContractEventSegmentId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['contract', 'contract_event_segments'],
        deletedIDFieldName: 'deletedContractEventSegmentId',
        connectionKeys: [
          {
            key: 'ContractContainer_contract_event_segments',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.eventSegments[idx].node.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  resetAddEventSegment() {
    const obj = _.cloneDeep(this.state);
    delete obj.newEventSegment;
    this.setState({ ...obj });
  }

  createEventSegment(index, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add event information',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      const obj = _.cloneDeep(this.state);
      delete obj.newEventSegment;
      this.setState({ ...obj });
      GlobalNotifications.show({
        message: 'Successfully added event information',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-contract-event-segment');
    };

    const mutation = graphql`
      mutation Schedule_Contract_createContractEventSegmentMutation(
        $input: CreateContractEventSegmentInput!
      ) {
        createContractEventSegment(input: $input) {
          new_contract_event_segment_edge {
            node {
              id
              title
              start_at
              end_at
              time_zone
              all_day
              location
              line1
              line2
              city
              province
              postal_code
              room
              notes
            }
          }
        }
      }
    `;

    const eventSegment = DateUtils.omitTimeZoneWithoutTimes(_.omit(
      nullifyEmptyStrings(this.state.newEventSegment.node),
      'all_day'
    ));

    eventSegment.title = eventSegment.title || '(Untitled Agenda Item)';

    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_contract_event_segment_edge',
        connectionInfo: [
          {
            key: 'ContractContainer_contract_event_segments',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          contract_id: parentID,
          ...eventSegment
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  addEventSegment(e) {
    e.preventDefault();
    const obj = _.cloneDeep(this.state);
    obj.editingNewEventSegment = true;
    if (!this.state.newEventSegment) {
      obj.newEventSegment = { node: {} };
    }
    this.setState({ ...obj });
  }

  newEventSegmentChangeHandler(eventSegment) {
    const obj = _.cloneDeep(this.state);
    obj.newEventSegment.node = eventSegment;
    this.setState({ ...obj });
  }

  closeNewEventSegmentEditor() {
    this.setState({ editingNewEventSegment: false });
  }

  render() {
    return (
      <Schedule
        addEventSegment={this.addEventSegment}
        closeNewEventSegmentEditor={this.closeNewEventSegmentEditor}
        createEventSegment={this.createEventSegment}
        deleteEventSegment={this.deleteEventSegment}
        eventSegments={this.props.eventSegments}
        eventSegmentChangeHandler={this.eventSegmentChangeHandler}
        editingNewEventSegment={this.state.editingNewEventSegment}
        localEventSegments={this.state.eventSegments}
        newEventSegment={this.state.newEventSegment}
        newEventSegmentChangeHandler={this.newEventSegmentChangeHandler}
        readOnly={this.props.readOnly}
        resetAddEventSegment={this.resetAddEventSegment}
        resetEditEventSegment={this.resetEditEventSegment}
        updateEventSegment={this.updateEventSegment}
        viewer={this.props.viewer}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Contract/Schedule.js